import api from "../../../services/api";
import {
  BackendEmailConfig,
  BackendRecipient,
  EmailFormData,
  EmailPreviewResponse,
  EmailSend,
  EmailVariablesCatalog,
} from "./types";
import { newRecipientKey } from "./template";

const BASE = "/easyReport/v2";

let catalogPromise: Promise<EmailVariablesCatalog> | null = null;

export const fetchEmailVariables = () => {
  if (!catalogPromise) {
    catalogPromise = api
      .get(`${BASE}/emailTemplateVariables`)
      .then((res: { data: EmailVariablesCatalog }) => res.data)
      .catch((error: unknown) => {
        catalogPromise = null;
        throw error;
      });
  }
  return catalogPromise;
};

export interface EmailContact {
  email: string;
  nome: string | null;
}

export const fetchEmailContacts = () =>
  api
    .get(`${BASE}/emailContacts`)
    .then((res: { data: EmailContact[] }) => res.data)
    .catch(() => [] as EmailContact[]);

export const previewEmailTemplate = (body: {
  agendamentoId?: string;
  assunto: string;
  corpo: string;
}) =>
  api
    .post(`${BASE}/previewEmailTemplate`, body)
    .then((res: { data: EmailPreviewResponse }) => res.data);

export const checkEmailHealth = () =>
  api
    .get(`${BASE}/emailHealth`)
    .then(() => true)
    .catch((error: any) => error?.response?.status !== 503);

export const findEmailConfig = (agendamentoId: string) =>
  api
    .post(`${BASE}/findEmailConfig`, { agendamentoId })
    .then(
      (res: {
        data: {
          possuiEmail: boolean;
          config: BackendEmailConfig | null;
          destinatarios: BackendRecipient[];
        };
      }) => res.data,
    );

export const upsertEmailConfig = (body: Record<string, unknown>) =>
  api.put(`${BASE}/upsertEmailConfig`, body);

export const findManyEmailSends = (body: {
  agendamentoId?: string;
  agendamentoRelatorioId?: string;
  pagina?: number;
  porPagina?: number;
}) =>
  api
    .post(`${BASE}/findManyEmailSends`, body)
    .then((res: { data: { total: number; envios: EmailSend[] } }) => res.data);

export const resendEmail = (body: {
  agendamentoRelatorioId: string;
  destinatario?: string;
}) => api.post(`${BASE}/resendEmail`, body);

export const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export const rejectedEmailsFromError = (error: any): string[] => {
  const message: string = error?.response?.data?.message ?? "";
  if (error?.response?.status !== 400 || !/e-?mail inv[aá]lido/i.test(message)) {
    return [];
  }
  return message
    .slice(message.indexOf(":") + 1)
    .split(/[,;\s]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
};

export const markRejected = (
  form: EmailFormData,
  rejected: string[],
): EmailFormData => ({
  ...form,
  recipients: form.recipients.map((r) =>
    rejected.includes(r.email.toLowerCase()) ? { ...r, rejeitado: true } : r,
  ),
});

export const emailFormFromBackend = (
  possuiEmail: boolean | undefined,
  config: BackendEmailConfig | null | undefined,
  destinatarios: BackendRecipient[] | undefined,
): EmailFormData => {
  const list = destinatarios ?? config?.Destinatarios ?? [];
  const custom = !!(config?.assunto || config?.corpo);
  return {
    enabled: !!possuiEmail,
    recipients: list.map((d) => ({
      key: d.id ?? newRecipientKey(),
      email: d.email,
      nome: d.nome ?? "",
      ativo: d.ativo !== false,
    })),
    mode: custom ? "custom" : "default",
    assunto: config?.assunto ?? "",
    corpo: config?.corpo ?? "",
  };
};

export const emailMessagePayload = (form: EmailFormData) => ({
  assunto: form.mode === "custom" && form.assunto.trim() ? form.assunto : null,
  corpo: form.mode === "custom" && form.corpo.trim() ? form.corpo : null,
});

export const emailRecipientsPayload = (form: EmailFormData) =>
  form.recipients.map((r) => ({
    email: r.email.trim(),
    ...(r.nome.trim() ? { nome: r.nome.trim() } : {}),
    ativo: r.ativo,
  }));

export const emailConfigForCreate = (form: EmailFormData) => ({
  ...emailMessagePayload(form),
  destinatarios: emailRecipientsPayload(form),
});

export const emailConfigForUpsert = (
  agendamentoId: string,
  form: EmailFormData,
) =>
  form.enabled
    ? {
        agendamentoId,
        possuiEmail: true,
        ...emailMessagePayload(form),
        destinatarios: emailRecipientsPayload(form),
      }
    : { agendamentoId, possuiEmail: false, ...emailMessagePayload(form) };
